import React, { useRef, useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Modal, Button, Chip, IconButton, Tooltip } from '@mui/material';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { product1, product2, product3, product4, product5, product6, product7, product8, product9, product10, product11, product12, product13, product14 } from '../assets';
import AerospaceManifold from '../assets/Aerospace Grade brass Manifold.png';
import CommanderSight from '../assets/Armoured battle tank Commander Sight Body.png';
import MountAssyRifle from '../assets/Mount Assy For Assault Rifle.png';
import SightUBGL from '../assets/Sight Assembly for UBGL.png';

const ProductCard = ({ title, image, description, specs, category, stats, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.5,
          delay: index * 0.1
        }
      });
    }
  }, [controls, inView, index]);

  const categoryColors = {
    'Defense': '#2196f3',
    'Aerospace': '#f50057',
    'Industrial': '#4caf50',
    'Military': '#ff9800',
    'Engineering': '#9c27b0'
  };

  return (
    <>
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={controls}
        whileHover={{ 
          scale: 1.05,
          transition: { duration: 0.2 }
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              paddingTop: '75%',
              overflow: 'hidden',
              '& img': {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              },
              '&:hover img': {
                transform: 'scale(1.1)'
              }
            }}
          >
            <img src={image} alt={title} />
            <Chip
              label={category}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: categoryColors[category],
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
          <Box sx={{ p: 3, flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 2
              }}
            >
              {description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {specs.slice(0, 3).map((spec, i) => (
                <Chip
                  key={i}
                  label={spec}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: categoryColors[category] }}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={() => setIsModalOpen(true)}
              sx={{
                bgcolor: categoryColors[category],
                '&:hover': {
                  bgcolor: categoryColors[category],
                  filter: 'brightness(0.9)'
                }
              }}
            >
              Learn More
            </Button>
          </Box>
        </Box>
      </motion.div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: 4,
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <IconButton
            onClick={() => setIsModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" sx={{ mb: 3, color: categoryColors[category] }}>
            {title}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <img src={image} alt={title} style={{ width: '100%', borderRadius: '10px' }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {description}
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>Specifications:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {specs.map((spec, i) => (
              <Chip key={i} label={spec} sx={{ mb: 1 }} />
            ))}
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Key Statistics:</Typography>
          <Grid container spacing={2}>
            {Object.entries(stats).map(([key, value], i) => (
              <Grid item xs={6} key={i}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '10px' }}>
                  <Typography variant="h4" sx={{ color: categoryColors[category] }}>
                    {value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {key}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const products = [
    {
      title: "Defense Components",
      image: product1,
      category: "Defense",
      description: "High-precision defense components engineered for maximum reliability and performance in critical operations. Our components meet stringent military specifications and are tested under extreme conditions.",
      specs: [
        "Military-grade materials",
        "Precision tolerance ±0.001mm",
        "Heat-treated steel alloy",
        "Anti-corrosion coating",
        "Shock-resistant design",
        "EMI shielding"
      ],
      stats: {
        "Durability": "15+ years",
        "Precision": "99.99%",
        "Success Rate": "100%",
        "Certification": "ISO 9001"
      }
    },
    {
      title: "Aerospace Parts",
      image: product2,
      category: "Aerospace",
      description: "Cutting-edge aerospace components designed for optimal performance in extreme conditions. Each part undergoes rigorous testing and certification processes to ensure reliability in aerospace applications.",
      specs: [
        "Aerospace-grade aluminum",
        "Temperature resistant",
        "Lightweight design",
        "High-altitude tested",
        "FAA certified",
        "Low maintenance"
      ],
      stats: {
        "Weight Reduction": "40%",
        "Strength": "2x",
        "Lifespan": "20+ years",
        "Safety Rating": "A+"
      }
    },
    {
      title: "Precision Machinery",
      image: product3,
      category: "Industrial",
      description: "State-of-the-art precision machinery built for manufacturing excellence. Our machines combine innovative technology with robust engineering to deliver superior performance and reliability.",
      specs: [
        "Smart automation",
        "Real-time monitoring",
        "Energy efficient",
        "IoT enabled",
        "Predictive maintenance",
        "24/7 operation capable"
      ],
      stats: {
        "Uptime": "99.9%",
        "Efficiency": "+85%",
        "ROI": "18 months",
        "Power Saving": "30%"
      }
    },
    {
      title: "Military Equipment",
      image: product4,
      category: "Military",
      description: "Advanced military equipment designed for tactical superiority and battlefield reliability. Built to withstand extreme conditions while maintaining peak performance.",
      specs: [
        "Tactical-grade materials",
        "Night vision compatible",
        "Weather-resistant",
        "Encrypted communications",
        "Quick deployment",
        "Field serviceable"
      ],
      stats: {
        "Reliability": "99.99%",
        "Battery Life": "72 hrs",
        "Range": "25 km",
        "Setup Time": "< 5 min"
      }
    },
    {
      title: "Industrial Solutions",
      image: product5,
      category: "Industrial",
      description: "Comprehensive industrial solutions that optimize manufacturing processes and enhance productivity. Our systems integrate seamlessly with existing infrastructure.",
      specs: [
        "Industry 4.0 ready",
        "Cloud connectivity",
        "Scalable design",
        "Remote monitoring",
        "Custom automation",
        "Safety certified"
      ],
      stats: {
        "Productivity": "+45%",
        "Quality": "99.9%",
        "Downtime": "-60%",
        "Energy Use": "-25%"
      }
    },
    {
      title: "Custom Engineering",
      image: product6,
      category: "Engineering",
      description: "Tailored engineering solutions designed to meet specific industry challenges. Our team of experts delivers innovative solutions that drive business success.",
      specs: [
        "Custom design",
        "Rapid prototyping",
        "3D modeling",
        "Performance analysis",
        "Quality testing",
        "Documentation"
      ],
      stats: {
        "Design Time": "-40%",
        "Success Rate": "95%",
        "Cost Saving": "35%",
        "Time to Market": "-50%"
      }
    },
    {
      title: "Advanced Navigation Systems",
      image: product7,
      category: "Defense",
      description: "State-of-the-art navigation systems with advanced GPS and inertial guidance capabilities. Designed for military and defense applications requiring precise positioning and navigation.",
      specs: [
        "Multi-constellation GNSS",
        "Inertial sensors",
        "Anti-jamming technology",
        "Encrypted data",
        "All-weather operation",
        "Redundant systems"
      ],
      stats: {
        "Accuracy": "< 1m",
        "Update Rate": "100Hz",
        "MTBF": "50,000 hrs",
        "Signal Lock": "< 30 sec"
      }
    },
    {
      title: "Satellite Components",
      image: product8,
      category: "Aerospace",
      description: "High-reliability satellite components engineered for space applications. Our components are radiation-hardened and qualified for long-term operation in space environments.",
      specs: [
        "Radiation-hardened",
        "Space-qualified materials",
        "Thermal management",
        "Low outgassing",
        "Vibration resistant",
        "Extended lifespan"
      ],
      stats: {
        "Radiation Tolerance": "100 krad",
        "Temperature Range": "-170°C to +125°C",
        "Reliability": "99.999%",
        "Mass Optimization": "60%"
      }
    },
    {
      title: "Autonomous Systems",
      image: product9,
      category: "Engineering",
      description: "Advanced autonomous systems featuring AI-powered decision making and robust control systems. Perfect for applications requiring intelligent automation and minimal human intervention.",
      specs: [
        "AI-powered control",
        "Real-time processing",
        "Sensor fusion",
        "Adaptive learning",
        "Fail-safe systems",
        "Remote monitoring"
      ],
      stats: {
        "Decision Speed": "< 10ms",
        "Accuracy": "99.9%",
        "Autonomy": "24/7",
        "Energy Efficiency": "+40%"
      }
    },
    {
      title: "Smart Manufacturing Solutions",
      image: product10,
      category: "Industrial",
      description: "Next-generation manufacturing solutions incorporating IoT, AI, and advanced robotics. Transform your manufacturing facility with intelligent automation and data-driven optimization.",
      specs: [
        "Digital twin technology",
        "AI optimization",
        "Robotic integration",
        "Real-time analytics",
        "Predictive quality",
        "Smart scheduling"
      ],
      stats: {
        "OEE Improvement": "+30%",
        "Quality Rate": "99.99%",
        "Lead Time": "-40%",
        "Resource Optimization": "+50%"
      }
    },
    {
      title: "Aerospace Grade brass Manifold",
      image: AerospaceManifold,
      category: "Aerospace",
      description: "High-precision aerospace grade brass manifold engineered for critical fluid and gas distribution systems. Manufactured to meet stringent aerospace standards with superior corrosion resistance and reliability.",
      specs: [
        "Aerospace-grade brass alloy",
        "High pressure rating",
        "Corrosion resistant",
        "Temperature tolerant",
        "Precision machined ports",
        "Leak-proof design"
      ],
      stats: {
        "Pressure Rating": "3000 PSI",
        "Temperature Range": "-54°C to +150°C",
        "Precision": "±0.005mm",
        "Service Life": "20+ years"
      }
    },
    {
      title: "Armoured battle tank Commander Sight Body",
      image: CommanderSight,
      category: "Military",
      description: "Advanced optical housing system for tank commander sights, providing superior protection and precision viewing capabilities. Engineered for extreme battlefield conditions while maintaining optical clarity.",
      specs: [
        "Ballistic-grade materials",
        "Shock-resistant design",
        "Anti-glare coating",
        "Thermal management",
        "NBC protection",
        "Quick-mount system"
      ],
      stats: {
        "Protection Level": "STANAG 4569",
        "Field of View": "360°",
        "Night Vision": "Gen 4",
        "Impact Resistance": "MIL-STD-810"
      }
    },
    {
      title: "Mount Assy For Assault Rifle",
      image: MountAssyRifle,
      category: "Defense",
      description: "Precision-engineered mounting system for modern assault rifles, providing stable and reliable attachment for various optical and tactical accessories. Designed for rapid deployment and maximum durability.",
      specs: [
        "MIL-SPEC materials",
        "Quick-release mechanism",
        "Zero-retention design",
        "Multi-rail interface",
        "All-weather coating",
        "Tool-less adjustment"
      ],
      stats: {
        "Weight": "< 250g",
        "Return to Zero": "100%",
        "Durability": "50,000 cycles",
        "Accuracy": "MOA retention"
      }
    },
    {
      title: "Sight Assembly for UBGL",
      image: SightUBGL,
      category: "Military",
      description: "Specialized sight assembly for Under Barrel Grenade Launcher (UBGL), offering enhanced accuracy and quick target acquisition. Features advanced optical design for various combat scenarios.",
      specs: [
        "Day/Night capability",
        "Ballistic compensation",
        "Waterproof design",
        "Anti-fog coating",
        "Shock-proof housing",
        "Quick zeroing system"
      ],
      stats: {
        "Range": "400m",
        "Battery Life": "1000 hrs",
        "Weight": "350g",
        "Magnification": "3x"
      }
    }
  ];

  const categories = ['All', ...new Set(products.map(p => p.category))];
  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 3, md: 6 },
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
         

          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 3,
              fontWeight: 700,
              color: 'primary.main'
            }}
          >
            Our Products
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{ m: 0.5 }}
              />
            ))}
          </Box>
        </motion.div>

        <AnimatePresence>
          <Grid container spacing={4}>
            {filteredProducts.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={product.title}>
                <ProductCard {...product} index={index} />
              </Grid>
            ))}
          </Grid>
        </AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <DotLottieReact
              src="https://lottie.host/cb14b7b2-f21a-45a9-80cb-27561fd0c81f/SweCXwADkk.lottie"
              loop
              autoplay
              style={{ width: '600px', height: '600px' }}
            />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(45deg, #1a472a 30%, #2e7d32 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Engineering Excellence
              </Typography>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '800px',
                  margin: '0 auto',
                  lineHeight: 1.8
                }}
              >
                Discover our cutting-edge solutions that push the boundaries of innovation.
                From defense to aerospace, we deliver precision-engineered products that define the future.
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Products;
